import { useState, useEffect, useCallback } from 'react';
import { isApiError } from '@aiptyg/types';
import * as settingsService from '../services/settings.service';
import type { HomeBannerItem, HomeBannerUpsertParams } from '../types/settings.types';
import { useToastStore } from '../../../store/toast.store';

function resolveError(e: unknown): string {
  if (isApiError(e)) {
    const map: Record<string, string> = {
      admin_role_required: '无管理员权限',
      'invalid home banner': 'Banner 数据无效，请检查字段',
      'home banner not found': '该 Banner 不存在或已被删除',
      network_error: '网络连接失败，请检查网络',
    };
    return map[e.message] ?? `请求失败 (${e.message})`;
  }
  return '未知错误';
}

export function useHomeBanners() {
  const addToast = useToastStore((s) => s.addToast);
  const [items, setItems] = useState<HomeBannerItem[]>([]);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await settingsService.getHomeBanners();
      setItems((result.items ?? []).map(normalizeBannerItem));
      setUpdatedAt(result.updated_at);
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = useCallback(async (params: HomeBannerUpsertParams) => {
    setSaving(true);
    try {
      const result = await settingsService.createHomeBanner(params);
      setItems((result.items ?? []).map(normalizeBannerItem));
      setUpdatedAt(result.updated_at);
      addToast('success', 'Banner 已添加');
      return true;
    } catch (e) {
      addToast('error', resolveError(e));
      return false;
    } finally {
      setSaving(false);
    }
  }, [addToast]);

  const update = useCallback(async (id: string, params: HomeBannerUpsertParams) => {
    setSaving(true);
    try {
      const result = await settingsService.updateHomeBanner(id, params);
      setItems((result.items ?? []).map(normalizeBannerItem));
      setUpdatedAt(result.updated_at);
      addToast('success', 'Banner 已更新');
      return true;
    } catch (e) {
      addToast('error', resolveError(e));
      return false;
    } finally {
      setSaving(false);
    }
  }, [addToast]);

  const remove = useCallback(async (id: string) => {
    setSaving(true);
    try {
      const result = await settingsService.deleteHomeBanner(id);
      setItems((result.items ?? []).map(normalizeBannerItem));
      setUpdatedAt(result.updated_at);
      addToast('success', 'Banner 已删除');
      return true;
    } catch (e) {
      addToast('error', resolveError(e));
      return false;
    } finally {
      setSaving(false);
    }
  }, [addToast]);

  return { items, updatedAt, loading, saving, error, reload: load, create, update, remove };
}

function normalizeBannerItem(item: HomeBannerItem): HomeBannerItem {
  return {
    ...item,
    market_id: item.market_id?.trim() || 'CN',
  };
}
